import { SectionHeader, Tooltip } from "../../../../../app/components";
import React from "react";
import styled from "styled-components";
import { useStudyPlan } from "../../../../../app/contexts/study.context";
import CountryMilestoneTable, {
  isPostFpeMilestone,
  isRemodelLockedMilestone,
} from "./country-milestone-table";
import SeparateContainer from "../../../../../app/components/separate.Container";
import SideBySide from "../../../../../app/components/sideBySide";

const MILESTONE_IDS = [158, 135, 136, 139, 142, 145, 146, 148, 149, 151, 152];

const hasLockedDownstream = (milestones, milestoneId) => {
  const idx = MILESTONE_IDS.indexOf(milestoneId);
  if (idx === -1) return false;
  const downstream = new Set(MILESTONE_IDS.slice(idx + 1));
  return milestones.some(
    (m) => downstream.has(m.milestoneId) && isRemodelLockedMilestone(m),
  );
};

const CountryGlobalMilestones = ({ country, siteGroup }) => {
  const { updateCountryMilestones, studyPlan } = useStudyPlan();
  const isRemodel = studyPlan?.studyPlanTypeDetail?.includes("Remodel");
  const { milestones, siteGroupId } = siteGroup;
  const { countryId } = country;

  const filteredMilestones = milestones.filter(
    (m) =>
      m.milestoneId === 158 ||
      m.milestoneId === 135 ||
      m.milestoneId === 136 ||
      m.milestoneId === 139 ||
      m.milestoneId === 142 ||
      m.milestoneId === 145 ||
      m.milestoneId === 148 ||
      m.milestoneId === 151 ||
      m.milestoneId === 146 ||
      m.milestoneId === 149 ||
      m.milestoneId === 152,
  );

  const leftSide = filteredMilestones.slice(0, 6);
  const rightSide = filteredMilestones.slice(7, 11);

  const handleChange = (milestoneName, property, value, initialValue) => {
    const milestone_index = milestones
      .map((m) => m.milestoneName)
      .indexOf(milestoneName);
    const milestone = milestones[milestone_index];

    if (isRemodelLockedMilestone(milestone) || isPostFpeMilestone(milestone)) {
      return;
    }

    if (property === "milestoneDateMillis") {
      const oldDate = milestone.milestoneDateMillis;
      milestone[property] = value;

      const lowerBound = Math.round(
        (value - initialValue) / (1000 * 60 * 60 * 24),
      );
      milestone.lowerBound = lowerBound;

      let new_milestones = [...milestones];
      new_milestones[milestone_index] = { ...milestone };

      if (
        milestoneName === "DURATION_TO_FPS" &&
        oldDate &&
        value !== oldDate &&
        !hasLockedDownstream(milestones, milestone.milestoneId)
      ) {
        const delta = value - oldDate;
        const downstream = new Set(
          MILESTONE_IDS.slice(
            MILESTONE_IDS.indexOf(milestone.milestoneId) + 1,
          ),
        );
        new_milestones = milestones.map((m, i) => {
          if (i === milestone_index) return { ...milestone };
          if (
            !downstream.has(m.milestoneId) ||
            isRemodelLockedMilestone(m) ||
            !m.milestoneDateMillis
          ) {
            return m;
          }
          return { ...m, milestoneDateMillis: m.milestoneDateMillis + delta };
        });
      }

      updateCountryMilestones(
        countryId,
        siteGroupId,
        { milestones: new_milestones },
        [milestone],
      );
      return;
    }

    milestone[property] = value;

    const new_milestones = [...milestones];
    new_milestones[milestone_index] = milestone;
    updateCountryMilestones(
      countryId,
      siteGroupId,
      { milestones: new_milestones },
      [milestone],
    );
  };

  return (
    <SeparateContainer>
      <Container id="country-milestones">
        <SideBySide>
          <SectionHeader
            paddingBottom={false}
            marginBottom={false}
            endAdornment={
              <Tooltip
                info="Check Override to edit one or more Milestone Date(s). Overridden dates remain fixed, and the system recalculates all other milestones automatically backward and forward. A comment is required for each override."
                changeColor={true}
              />
            }
          >
            Country Milestones
          </SectionHeader>
        </SideBySide>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "8px",
            padding: "20px",
          }}
        >
          <CountryMilestoneTable
            rows={leftSide}
            handleChange={handleChange}
            isRemodel={isRemodel}
          />
          <CountryMilestoneTable
            rows={rightSide}
            isRightOrLeft={"right"}
            handleChange={handleChange}
            isRemodel={isRemodel}
          />
        </div>
      </Container>
    </SeparateContainer>
  );
};

export default CountryGlobalMilestones;

const Container = styled.div``;
